import React, { useContext, useMemo, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import ReactTable from "react-table";
import { Doughnut } from "react-chartjs-2";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
} from "reactstrap";
import classnames from "classnames";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";

const STATUSES = [
  ["", "shopee.workspace.orders.status.all", "primary"],
  ["UNPAID", "shopee.workspace.orders.status.unpaid", "warning"],
  ["READY_TO_SHIP", "shopee.workspace.orders.status.ready-to-ship", "warning"],
  ["PROCESSED", "shopee.workspace.orders.status.processed", "info"],
  ["SHIPPED", "shopee.workspace.orders.status.shipped", "primary"],
  [
    "TO_CONFIRM_RECEIVE",
    "shopee.workspace.orders.status.to-confirm-receive",
    "info",
  ],
  ["COMPLETED", "shopee.workspace.orders.status.completed", "success"],
  ["IN_CANCEL", "shopee.workspace.orders.status.in-cancel", "danger"],
  ["CANCELLED", "shopee.workspace.orders.status.cancelled", "danger"],
];

const STATUS_MAP = STATUSES.reduce(
  (result, status) => ({
    ...result,
    [status[0]]: { label: status[1], color: status[2] },
  }),
  {},
);

const CHART_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#2563eb",
  "#64748b",
  "#94a3b8",
];

const CANCELLED_STATUSES = new Set(["IN_CANCEL", "CANCELLED"]);
const SHIPPING_DEADLINE_STATUSES = new Set(["READY_TO_SHIP", "PROCESSED"]);

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

export const excludeDemoOrders = (orders) =>
  Array.isArray(orders)
    ? orders.filter((order) => order && order.is_demo !== true)
    : [];

export const formatMoney = (value, currency) => {
  if (!hasValue(value)) return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  const normalizedCurrency = String(currency || "")
    .trim()
    .toUpperCase();
  const options = normalizedCurrency
    ? {
        style: "currency",
        currency: normalizedCurrency,
        maximumFractionDigits: 0,
      }
    : { maximumFractionDigits: 0 };
  try {
    return new Intl.NumberFormat("vi-VN", options).format(amount);
  } catch (error) {
    return `${new Intl.NumberFormat("vi-VN", {
      maximumFractionDigits: 0,
    }).format(amount)}${normalizedCurrency ? ` ${normalizedCurrency}` : ""}`;
  }
};

const dateParts = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return ["—", ""];
  return [
    date.toLocaleDateString("vi-VN"),
    date.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }),
  ];
};

export const getOrderItemCount = (order) =>
  Array.isArray(order.items)
    ? order.items.reduce((total, item) => {
        const quantity = Number(
          item.model_quantity_purchased ??
            item.quantity_purchased ??
            item.quantity,
        );
        return (
          total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0)
        );
      }, 0)
    : 0;

export const buildOrderPageMetrics = (orders, now = Date.now()) => {
  const rows = Array.isArray(orders) ? orders : [];
  const nowTime = Number.isFinite(Number(now)) ? Number(now) : Date.now();
  const deadlineLimit = nowTime + 24 * 60 * 60 * 1000;
  const counts = {};
  const gmvByCurrency = {};
  let gmvEligibleOrders = 0;
  let gmvOrders = 0;
  let fulfillmentOrders = 0;
  let shippingDeadlineOrders = 0;
  let deadlineRiskOrders = 0;
  let overdueOrders = 0;

  rows.forEach((order) => {
    const status = String(order.order_status || "UNKNOWN").toUpperCase();
    counts[status] = (counts[status] || 0) + 1;

    if (!CANCELLED_STATUSES.has(status)) {
      gmvEligibleOrders += 1;
      if (hasValue(order.total_amount)) {
        const amount = Number(order.total_amount);
        if (Number.isFinite(amount)) {
          const currency = String(order.currency || "")
            .trim()
            .toUpperCase();
          gmvByCurrency[currency] = (gmvByCurrency[currency] || 0) + amount;
          gmvOrders += 1;
        }
      }
    }

    if (SHIPPING_DEADLINE_STATUSES.has(status)) {
      fulfillmentOrders += 1;
      const deadline = new Date(order.ship_by_date).getTime();
      if (order.ship_by_date && Number.isFinite(deadline)) {
        shippingDeadlineOrders += 1;
        if (deadline <= deadlineLimit) deadlineRiskOrders += 1;
        if (deadline <= nowTime) overdueOrders += 1;
      }
    }
  });

  return {
    counts,
    gmvByCurrency,
    gmvEligibleOrders,
    gmvOrders,
    fulfillmentOrders,
    shippingDeadlineOrders,
    deadlineRiskOrders,
    overdueOrders,
    completedOrders: counts.COMPLETED || 0,
  };
};

export const formatOrderGmv = (metrics) => {
  const entries = Object.entries(metrics.gmvByCurrency || {});
  if (!entries.length) return metrics.gmvEligibleOrders ? "—" : "0";
  return entries
    .map(([currency, total]) => formatMoney(total, currency))
    .join(" + ");
};

const percentage = (value, total) =>
  total ? Math.round((Number(value || 0) / total) * 100) : 0;

const exportOrders = (orders, headers) => {
  const quote = (value) =>
    `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
  const rows = orders.map((order) => [
    order.order_sn,
    order.recipient_name || order.buyer_username,
    order.recipient_phone,
    getOrderItemCount(order),
    order.total_amount,
    order.order_status,
    order.tracking_number,
    order.order_create_time,
  ]);
  const csv = [headers, ...rows]
    .map((row) => row.map(quote).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(
    new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = `shopee-orders-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const MetricCard = ({
  label,
  value,
  indicator,
  indicatorTone = "success",
  note,
  icon,
  iconTone = "blue",
}) => (
  <Card
    className="h-100 border-0 shadow-sm overflow-hidden"
    style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
  >
    <CardBody className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span
          className="small text-muted font-weight-bold"
          style={{ fontSize: "11.5px" }}
        >
          {label}
        </span>
        {icon && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background:
                iconTone === "orange"
                  ? "rgba(245, 158, 11, 0.12)"
                  : iconTone === "purple"
                    ? "rgba(139, 92, 246, 0.12)"
                    : iconTone === "green"
                      ? "rgba(16, 185, 129, 0.12)"
                      : "rgba(37, 99, 235, 0.12)",
              color:
                iconTone === "orange"
                  ? "#f59e0b"
                  : iconTone === "purple"
                    ? "#8b5cf6"
                    : iconTone === "green"
                      ? "#10b981"
                      : "#2563eb",
            }}
          >
            <i
              className={`${icon} font-weight-bold`}
              style={{ fontSize: 13 }}
            />
          </div>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-baseline">
        <strong className="h4 mb-0 font-weight-bold text-dark">{value}</strong>
        <small
          className={`font-weight-bold bg-light px-2 py-1 rounded ${
            indicatorTone === "danger"
              ? "text-danger"
              : indicatorTone === "warning"
                ? "text-warning"
                : indicatorTone === "neutral"
                  ? "text-muted"
                  : "text-success"
          }`}
          style={{ fontSize: "10px" }}
        >
          {indicator}
        </small>
      </div>
      <small className="text-muted d-block mt-2" style={{ fontSize: "10.5px" }}>
        {note}
      </small>
    </CardBody>
  </Card>
);

export default injectIntl(function ShopeeOrdersPanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const orders = useMemo(
    () => excludeDemoOrders(workspace.data.orders),
    [workspace.data.orders],
  );
  const filters = workspace.filters || {};
  const meta = workspace.meta || {};
  const selection = workspace.selection || [];
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const statusTabsRef = useRef(null);
  const statusTabsDragRef = useRef(null);
  const statusTabsDidDragRef = useRef(false);

  const metrics = useMemo(() => buildOrderPageMetrics(orders), [orders]);
  const counts = metrics.counts;
  const parsedTotal = Number(meta.total);
  const parsedPage = Number(meta.page);
  const parsedTotalPages = Number(meta.total_pages);
  const parsedPageSize = Number(meta.page_size);
  const total = Number.isFinite(parsedTotal) ? parsedTotal : orders.length;
  const page = Number.isFinite(parsedPage)
    ? parsedPage
    : Number(filters.page || 1);
  const totalPages = Math.max(
    1,
    Number.isFinite(parsedTotalPages) ? parsedTotalPages : 1,
  );
  const pageSize = Number.isFinite(parsedPageSize)
    ? parsedPageSize
    : Number(filters.page_size || 20);
  const pendingOrders = (counts.READY_TO_SHIP || 0) + (counts.PROCESSED || 0);
  const deadlineValue = metrics.shippingDeadlineOrders
    ? metrics.deadlineRiskOrders
    : metrics.fulfillmentOrders
      ? "—"
      : 0;
  const deadlineIndicator = metrics.overdueOrders
    ? intl.formatMessage(
        { id: "shopee.workspace.orders.metric.deadline-overdue" },
        { count: metrics.overdueOrders },
      )
    : metrics.fulfillmentOrders
      ? intl.formatMessage(
          { id: "shopee.workspace.orders.metric.deadline-window" },
          {
            risk: metrics.shippingDeadlineOrders,
            total: metrics.fulfillmentOrders,
          },
        )
      : intl.formatMessage({
          id: "shopee.workspace.orders.metric.deadline-none",
        });
  const exportHeaders = [
    intl.formatMessage({ id: "shopee.workspace.orders.csv.order-code" }),
    intl.formatMessage({ id: "shopee.workspace.common.customer" }),
    intl.formatMessage({ id: "shopee.workspace.orders.csv.phone" }),
    intl.formatMessage({ id: "shopee.workspace.orders.csv.products" }),
    intl.formatMessage({ id: "shopee.workspace.orders.csv.amount" }),
    intl.formatMessage({ id: "shopee.workspace.returns.col.status" }),
    intl.formatMessage({ id: "shopee.workspace.orders.col.tracking" }),
    intl.formatMessage({ id: "shopee.workspace.returns.col.created-at" }),
  ];
  const chartLabels = {};
  STATUSES.forEach((status) => {
    if (status[0]) {
      chartLabels[status[0]] = intl.formatMessage({ id: status[1] });
    }
  });

  const updateFilters = (changes) =>
    workspace.setFilters({ ...filters, page: 1, ...changes });

  const beginStatusTabsDrag = (event) => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    const scrollContainer = statusTabsRef.current;
    if (!scrollContainer) return;
    statusTabsDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: scrollContainer.scrollLeft,
    };
    statusTabsDidDragRef.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const dragStatusTabs = (event) => {
    const drag = statusTabsDragRef.current;
    const scrollContainer = statusTabsRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !scrollContainer) return;
    const distance = event.clientX - drag.startX;
    if (Math.abs(distance) > 3) {
      statusTabsDidDragRef.current = true;
      event.preventDefault();
    }
    scrollContainer.scrollLeft = drag.scrollLeft - distance;
  };

  const endStatusTabsDrag = (event) => {
    const drag = statusTabsDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const didDrag = statusTabsDidDragRef.current;
    statusTabsDragRef.current = null;
    if (didDrag) {
      window.setTimeout(() => {
        statusTabsDidDragRef.current = false;
      }, 0);
    }
  };

  const toggleRow = (id) =>
    workspace.setSelection(
      selection.includes(id)
        ? selection.filter((value) => value !== id)
        : [...selection, id],
    );

  const toggleAll = () => {
    const ids = orders.map((order) => order.id);
    const checked = ids.length > 0 && ids.every((id) => selection.includes(id));
    workspace.setSelection(
      checked
        ? selection.filter((id) => !ids.includes(id))
        : Array.from(new Set([...selection, ...ids])),
    );
  };

  const openDetail = async (order) => {
    setDetail({ ...order, items: order.items || [] });
    setDetailLoading(true);
    try {
      const response = await ShopeeV2Api.order(order.id);
      setDetail(response.data || order);
    } catch (error) {
      setDetail({ ...order, items: order.items || [], loadError: true });
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    {
      id: "selection",
      Header: () => (
        <Input
          type="checkbox"
          aria-label={intl.formatMessage({
            id: "shopee.workspace.orders.select-all-aria",
          })}
          checked={
            orders.length > 0 &&
            orders.every((order) => selection.includes(order.id))
          }
          onChange={toggleAll}
        />
      ),
      Cell: ({ original }) => (
        <Input
          type="checkbox"
          aria-label={intl.formatMessage(
            { id: "shopee.workspace.orders.select-row-aria" },
            { sn: original.order_sn },
          )}
          checked={selection.includes(original.id)}
          onChange={() => toggleRow(original.id)}
        />
      ),
      sortable: false,
      width: 44,
    },
    {
      Header: <IntlMessages id="shopee.workspace.orders.col.sn" />,
      accessor: "order_sn",
      minWidth: 140,
      Cell: ({ value }) => (
        <strong className="text-primary font-weight-bold">{value}</strong>
      ),
    },
    {
      Header: <IntlMessages id="shopee.workspace.orders.col.channel" />,
      accessor: "shop_id",
      width: 95,
      Cell: () => (
        <Badge color="warning" pill style={{ background: "#EE4D2D" }}>
          Shopee
        </Badge>
      ),
    },
    {
      Header: <IntlMessages id="shopee.workspace.common.customer" />,
      id: "customer",
      minWidth: 140,
      accessor: (order) => order.recipient_name || order.buyer_username || "—",
      Cell: ({ original, value }) => (
        <div>
          <strong className="text-dark d-block">{value}</strong>
          <small className="text-muted">
            {original.recipient_phone || (
              <IntlMessages id="shopee.workspace.orders.phone-hidden" />
            )}
          </small>
        </div>
      ),
    },
    {
      Header: <IntlMessages id="shopee.workspace.orders.col.items" />,
      id: "products",
      accessor: getOrderItemCount,
      width: 100,
      Cell: ({ value }) => (
        <div>
          <strong className="text-dark">
            <IntlMessages
              id="shopee.workspace.orders.items-count"
              values={{ count: value || 0 }}
            />
          </strong>
          {value ? (
            <small className="text-muted d-block">
              <IntlMessages
                id="shopee.workspace.orders.items-total-x"
                values={{ count: value }}
              />
            </small>
          ) : null}
        </div>
      ),
    },
    {
      Header: <IntlMessages id="shopee.workspace.orders.col.amount" />,
      accessor: "total_amount",
      minWidth: 125,
      Cell: ({ original, value }) => (
        <div>
          <strong className="text-dark font-weight-bold">
            {formatMoney(value, original.currency)}
          </strong>
          <small className="text-muted d-block">
            {original.order_paid_time ? (
              <IntlMessages
                id="shopee.workspace.orders.paid-time"
                values={{
                  time: dateParts(original.order_paid_time).join(" ").trim(),
                }}
              />
            ) : (
              <IntlMessages id="shopee.workspace.orders.paid-time-missing" />
            )}
          </small>
        </div>
      ),
    },
    {
      Header: <IntlMessages id="shopee.workspace.returns.col.status" />,
      accessor: "order_status",
      minWidth: 130,
      Cell: ({ value }) => {
        const known =
          STATUS_MAP[String(value || "").toUpperCase()] || null;
        return (
          <div>
            <Badge
              color={known ? known.color : "secondary"}
              pill
              className="px-2 py-1"
            >
              {known ? (
                <IntlMessages id={known.label} />
              ) : (
                value || intl.formatMessage({
                  id: "shopee.workspace.orders.status-unknown",
                })
              )}
            </Badge>
            <small className="text-muted d-block mt-1 font-weight-bold">
              {value || "UNKNOWN"}
            </small>
          </div>
        );
      },
    },
    {
      Header: <IntlMessages id="shopee.workspace.orders.col.tracking" />,
      accessor: "tracking_number",
      minWidth: 130,
      Cell: ({ original, value }) => (
        <div>
          <strong className="text-dark">{value || "—"}</strong>
          {original.shipping_carrier ? (
            <small className="text-muted d-block">
              {original.shipping_carrier}
            </small>
          ) : null}
        </div>
      ),
    },
    {
      Header: <IntlMessages id="shopee.workspace.orders.col.time" />,
      accessor: "order_create_time",
      width: 105,
      Cell: ({ value }) => {
        const parts = dateParts(value);
        return (
          <div>
            <span className="text-dark font-weight-semibold">{parts[0]}</span>
            <small className="text-muted d-block">{parts[1]}</small>
          </div>
        );
      },
    },
    {
      id: "actions",
      Header: <IntlMessages id="shopee.workspace.actions" />,
      sortable: false,
      width: 85,
      Cell: ({ original }) => (
        <Button
          color="outline-primary"
          outline
          size="sm"
          className="rounded-pill px-3"
          onClick={() => openDetail(original)}
        >
          <IntlMessages id="shopee.workspace.common.detail" />
        </Button>
      ),
    },
  ];

  const statusOrder = STATUSES.slice(1).map((status) => status[0]);
  const unknownStatusText = intl.formatMessage({
    id: "shopee.workspace.orders.status-unknown",
  });
  const chartRows = Object.entries(counts)
    .map(([value, count]) => {
      const known = STATUS_MAP[value];
      return {
        value,
        labelId: known ? known.label : null,
        labelText: known ? chartLabels[value] || value : value,
        color: known ? known.color : "secondary",
        count,
      };
    })
    .sort((left, right) => {
      const leftIndex = statusOrder.indexOf(left.value);
      const rightIndex = statusOrder.indexOf(right.value);
      return (
        (leftIndex < 0 ? statusOrder.length : leftIndex) -
        (rightIndex < 0 ? statusOrder.length : rightIndex)
      );
    });

  const chartData = {
    labels: chartRows.map((status) => status.labelText),
    datasets: [
      {
        data: chartRows.map((status) => status.count),
        backgroundColor: chartRows.map(
          (status, index) => CHART_COLORS[index % CHART_COLORS.length],
        ),
        borderWidth: 0,
      },
    ],
  };
  const knownDetailStatus = detail
    ? STATUS_MAP[String(detail.order_status || "").toUpperCase()]
    : null;
  const detailStatus = detail
    ? knownDetailStatus || {
        label: null,
        text: detail.order_status || unknownStatusText,
        color: "secondary",
      }
    : { label: null, text: unknownStatusText, color: "secondary" };

  return (
    <div className="mb-4">
      {/* Metric Cards Header */}
      <Row className="mb-3">
        <Col xl="2" md="6" className="mb-3 mb-xl-0">
          <MetricCard
            label={intl.formatMessage({ id: "shopee.workspace.orders.metric.total" })}
            value={total.toLocaleString("vi-VN")}
            indicator={intl.formatMessage(
              { id: "shopee.workspace.orders.metric.total-indicator" },
              { count: orders.length },
            )}
            indicatorTone="neutral"
            // note="Dữ liệu đã đồng bộ từ Shopee"
            icon="iconsminds-shopping-basket"
            iconTone="blue"
          />
        </Col>
        <Col xl="2" md="6" className="mb-3 mb-xl-0">
          <MetricCard
            label={intl.formatMessage({ id: "shopee.workspace.orders.metric.pending" })}
            value={pendingOrders}
            indicator={intl.formatMessage(
              { id: "shopee.workspace.orders.metric.pending-indicator" },
              { percent: percentage(pendingOrders, orders.length) },
            )}
            indicatorTone={pendingOrders ? "warning" : "success"}
            // note="READY_TO_SHIP / PROCESSED"
            icon="iconsminds-box-close"
            iconTone="orange"
          />
        </Col>
        <Col xl="2" md="4" className="mb-3 mb-xl-0">
          <MetricCard
            label={intl.formatMessage({ id: "shopee.workspace.orders.metric.deadline" })}
            value={deadlineValue}
            indicator={deadlineIndicator}
            indicatorTone={
              metrics.overdueOrders
                ? "danger"
                : metrics.shippingDeadlineOrders < metrics.fulfillmentOrders
                  ? "warning"
                  : "success"
            }
            // note="Quá hạn hoặc trong 24 giờ theo Shopee"
            icon="iconsminds-clock"
            iconTone="purple"
          />
        </Col>
        <Col xl="4" md="4" className="mb-3 mb-xl-0">
          <MetricCard
            label={intl.formatMessage({ id: "shopee.workspace.orders.metric.gmv" })}
            value={formatOrderGmv(metrics)}
            indicator={intl.formatMessage(
              { id: "shopee.workspace.orders.metric.gmv-indicator" },
              {
                paid: metrics.gmvOrders,
                eligible: metrics.gmvEligibleOrders,
              },
            )}
            indicatorTone={
              metrics.gmvOrders < metrics.gmvEligibleOrders
                ? "warning"
                : "success"
            }
            // note="Không gồm CANCELLED / IN_CANCEL"
            icon="iconsminds-money-bag"
            iconTone="green"
          />
        </Col>
        <Col xl="2" md="4">
          <MetricCard
            label={intl.formatMessage({ id: "shopee.workspace.orders.metric.delivered" })}
            value={metrics.completedOrders}
            indicator={intl.formatMessage(
              { id: "shopee.workspace.orders.metric.pending-indicator" },
              { percent: percentage(metrics.completedOrders, orders.length) },
            )}
            // note="Trạng thái COMPLETED từ Shopee"
            icon="simple-icon-target"
            iconTone="blue"
          />
        </Col>
      </Row>

      <Row>
        <Col xl="9" className="min-width-zero">
          {/* Status Pipeline Navigation */}
          <Card
            className="mb-3 border-0 shadow-sm overflow-hidden"
            style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
          >
            <CardBody className="p-2">
              <div
                ref={statusTabsRef}
                className="w-100"
                role="region"
                aria-label={intl.formatMessage({
                  id: "shopee.workspace.orders.tabs-region-aria",
                })}
                onPointerDown={beginStatusTabsDrag}
                onPointerMove={dragStatusTabs}
                onPointerUp={endStatusTabsDrag}
                onPointerCancel={endStatusTabsDrag}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  WebkitOverflowScrolling: "touch",
                  cursor: "grab",
                  userSelect: "none",
                }}
              >
                <Nav
                  pills
                  className="flex-nowrap py-1"
                  style={{ minWidth: "100%", width: "max-content" }}
                >
                  {STATUSES.map((status) => {
                    const isActive = (filters.status || "") === status[0];
                    const statusCount = status[0]
                      ? counts[status[0]] || 0
                      : orders.length;
                    return (
                      <NavItem
                        key={status[0] || "all"}
                        className="mr-1 flex-shrink-0"
                      >
                        <NavLink
                          href="#"
                          className={classnames(
                            "d-flex align-items-center px-3 py-2 rounded-pill font-weight-semibold transition-all text-nowrap",
                            {
                              active: isActive,
                            },
                          )}
                          style={{
                            fontSize: "12px",
                            background: isActive ? "#EE4D2D" : "transparent",
                            color: isActive ? "#ffffff" : "#475569",
                            boxShadow: isActive
                              ? "0 4px 12px rgba(238, 77, 45, 0.25)"
                              : "none",
                            textDecoration: "none",
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            if (statusTabsDidDragRef.current) return;
                            updateFilters({ status: status[0] });
                          }}
                        >
                          <span>
                            <IntlMessages id={status[1]} />
                          </span>
                          <Badge
                            pill
                            className="ml-2"
                            title={intl.formatMessage({
                              id: "shopee.workspace.orders.count-tooltip",
                            })}
                            style={{
                              fontSize: "10px",
                              background: isActive ? "#ffffff" : "#e2e8f0",
                              color: isActive ? "#EE4D2D" : "#64748b",
                            }}
                          >
                            {statusCount}
                          </Badge>
                        </NavLink>
                      </NavItem>
                    );
                  })}
                </Nav>
              </div>
            </CardBody>
          </Card>

          {/* Main Table Card */}
          <Card
            className="border-0 shadow-sm overflow-hidden"
            style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
          >
            <CardBody className="p-3">
              <Row className="align-items-center">
                <Col lg="2" md="4" className="mb-2">
                  <Button
                    block
                    color={filtersOpen ? "primary" : "outline-secondary"}
                    className="rounded-pill"
                    onClick={() => setFiltersOpen((value) => !value)}
                  >
                    <i className="simple-icon-filter mr-1" />{" "}
                    <IntlMessages id="shopee.workspace.common.filter" />
                  </Button>
                </Col>
                <Col lg="4" md="8" className="mb-2">
                  <div className="d-flex align-items-center">
                    <Input
                      type="date"
                      value={filters.from || ""}
                      className="rounded-pill"
                      style={{ fontSize: "12px" }}
                      onChange={(event) =>
                        updateFilters({ from: event.target.value })
                      }
                    />
                    <span className="mx-2 text-muted">-</span>
                    <Input
                      type="date"
                      value={filters.to || ""}
                      className="rounded-pill"
                      style={{ fontSize: "12px" }}
                      onChange={(event) =>
                        updateFilters({ to: event.target.value })
                      }
                    />
                  </div>
                </Col>
                <Col lg="6" md="12" className="mb-2 text-lg-right">
                  <Button
                    outline
                    color="secondary"
                    className="mr-2 rounded-pill px-3"
                    disabled={!orders.length}
                    onClick={() => exportOrders(orders, exportHeaders)}
                  >
                    <i className="simple-icon-arrow-down-circle mr-1" />{" "}
                    <IntlMessages id="shopee.workspace.returns.export-excel" />
                  </Button>
                  <Button
                    color="primary"
                    className="rounded-pill px-3"
                    outline
                    style={{ borderColor: "#EE4D2D", color: "#EE4D2D" }}
                    disabled={workspace.busy["sync-orders"]}
                    onClick={() =>
                      workspace.runAction(
                        "sync-orders",
                        (key) =>
                          ShopeeV2Api.syncOrders(workspace.selectedShopId, key),
                        intl.formatMessage({
                          id: "shopee.workspace.orders.sync-queued",
                        }),
                      )
                    }
                  >
                    <i
                      className={`simple-icon-refresh mr-1 ${workspace.busy["sync-orders"] ? "spin" : ""}`}
                    />
                    <IntlMessages id="shopee.workspace.orders.sync-button" />
                  </Button>
                </Col>
              </Row>
            </CardBody>

            {/* Selection Banner */}
            {selection.length ? (
              <Alert
                color="primary"
                className="mb-0 d-flex align-items-center justify-content-between py-2 px-3 rounded-0"
                style={{
                  background: "#eff6ff",
                  borderColor: "#bfdbfe",
                  color: "#1e40af",
                }}
              >
                <span>
                  <IntlMessages
                    id="shopee.workspace.orders.selection-banner"
                    values={{ count: selection.length }}
                  />
                </span>
                <div>
                  <Button
                    color="primary"
                    size="sm"
                    className="mr-2 rounded-pill px-3"
                    onClick={() =>
                      exportOrders(
                        orders.filter((o) => selection.includes(o.id)),
                        exportHeaders,
                      )
                    }
                  >
                    <IntlMessages id="shopee.workspace.orders.export-selected" />
                  </Button>
                  <Button
                    color="link"
                    size="sm"
                    className="text-muted p-0"
                    onClick={() => workspace.setSelection([])}
                  >
                    <IntlMessages id="shopee.workspace.orders.clear-selection" />
                  </Button>
                </div>
              </Alert>
            ) : null}

            <ReactTable
              data={orders}
              columns={columns}
              minRows={0}
              pageSize={Math.max(orders.length, 1)}
              showPagination={false}
              className="-highlight border-0"
              NoDataComponent={() => (
                <div className="text-center text-muted py-5">
                  <i
                    className="iconsminds-shopping-basket d-block mb-2"
                    style={{ fontSize: "32px", opacity: 0.4 }}
                  />
                  <IntlMessages id="shopee.workspace.orders.empty" />
                </div>
              )}
            />

            <CardBody className="border-top p-3">
              <Row className="align-items-center">
                <Col md="4" className="small text-muted mb-2 mb-md-0">
                  <IntlMessages
                    id="shopee.workspace.orders.showing-summary"
                    values={{
                      shown: orders.length,
                      total: total.toLocaleString("vi-VN"),
                    }}
                  />
                </Col>
                <Col md="4" className="text-center mb-2 mb-md-0">
                  <ButtonGroup>
                    <Button
                      color="light"
                      size="sm"
                      outline
                      disabled={page <= 1}
                      className="rounded-left"
                      onClick={() =>
                        workspace.setFilters({ ...filters, page: page - 1 })
                      }
                    >
                      ‹{" "}
                      <IntlMessages id="shopee.workspace.common.previous" />
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      disabled
                      style={{ background: "#EE4D2D", border: "none" }}
                    >
                      {page}
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      outline
                      disabled={page >= totalPages}
                      className="rounded-right"
                      onClick={() =>
                        workspace.setFilters({ ...filters, page: page + 1 })
                      }
                    >
                      <IntlMessages id="shopee.workspace.common.next" /> ›
                    </Button>
                  </ButtonGroup>
                  <small className="text-muted ml-2">
                    <IntlMessages
                      id="shopee.workspace.common.page-count"
                      values={{ total: totalPages }}
                    />
                  </small>
                </Col>
                <Col md="4" className="text-md-right">
                  <Input
                    type="select"
                    value={pageSize}
                    className="d-inline-block w-auto rounded-pill"
                    style={{ fontSize: "12px" }}
                    onChange={(event) =>
                      workspace.setFilters({
                        ...filters,
                        page: 1,
                        page_size: Number(event.target.value),
                      })
                    }
                  >
                    {[10, 20, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        <IntlMessages
                          id="shopee.workspace.common.per-page"
                          values={{ size }}
                        />
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* Sidebar Filters & Chart */}
        <Col
          xl="3"
          className={classnames("mt-3 mt-xl-0", {
            "d-none d-xl-block": !filtersOpen,
          })}
        >
          <Card
            className="mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
          >
            <CardHeader className="bg-transparent border-bottom d-flex justify-content-between align-items-center py-3">
              <strong className="text-dark font-weight-bold">
                <IntlMessages id="shopee.workspace.orders.filter-title" />
              </strong>
              <Button
                color="link"
                size="sm"
                className="p-0 text-primary font-weight-semibold"
                onClick={() =>
                  updateFilters({
                    status: "",
                    search: "",
                    from: "",
                    to: "",
                  })
                }
              >
                <IntlMessages id="shopee.workspace.orders.clear-filters" />
              </Button>
            </CardHeader>
            <CardBody>
              <FormGroup className="mb-3">
                <Label className="small font-weight-bold text-muted">
                  <IntlMessages id="shopee.workspace.orders.filter-status-label" />
                </Label>
                <Input
                  type="select"
                  value={filters.status || ""}
                  className="rounded-pill"
                  style={{ fontSize: "12px" }}
                  onChange={(event) =>
                    updateFilters({ status: event.target.value })
                  }
                >
                  {STATUSES.map((status) => (
                    <option key={status[0] || "all"} value={status[0]}>
                      <IntlMessages id={status[1]} />
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup className="mb-3">
                <Label className="small font-weight-bold text-muted">
                  <IntlMessages id="shopee.workspace.orders.filter-search-label" />
                </Label>
                <Input
                  value={filters.search || ""}
                  placeholder={intl.formatMessage({
                    id: "shopee.workspace.orders.search-placeholder",
                  })}
                  className="rounded-pill"
                  style={{ fontSize: "12px" }}
                  onChange={(event) =>
                    updateFilters({ search: event.target.value })
                  }
                />
              </FormGroup>
              <Button
                color="primary"
                block
                className="rounded-pill font-weight-bold"
                style={{ background: "#EE4D2D", border: "none" }}
                onClick={workspace.load}
              >
                <IntlMessages id="shopee.workspace.orders.apply-filters" />
              </Button>
            </CardBody>
          </Card>

          <Card
            className="border-0 shadow-sm"
            style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
          >
            <CardHeader className="bg-transparent border-bottom py-3">
              <strong className="text-dark font-weight-bold">
                <IntlMessages id="shopee.workspace.orders.chart-title" />
              </strong>
            </CardHeader>
            <CardBody>
              {chartRows.length ? (
                <Doughnut
                  data={chartData}
                  height={190}
                  options={{
                    maintainAspectRatio: false,
                    legend: { display: false },
                    cutoutPercentage: 68,
                  }}
                />
              ) : (
                <div className="text-center text-muted py-4">
                  <IntlMessages id="shopee.workspace.common.no-data" />
                </div>
              )}
              <div className="mt-3">
                {chartRows.map((status) => (
                  <div
                    key={status.value}
                    className="d-flex justify-content-between align-items-center small mb-2 p-2 rounded"
                    style={{ background: "#f8fafc" }}
                  >
                    <span>
                      <Badge pill color={status.color} className="mr-2">
                        {status.count}
                      </Badge>
                      <strong className="text-dark">{status.labelText}</strong>
                    </span>
                    <span className="text-muted font-weight-semibold">
                      {orders.length
                        ? Math.round((status.count / orders.length) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Order Detail Modal */}
      <Modal
        isOpen={Boolean(detail)}
        toggle={() => setDetail(null)}
        size="lg"
        className="modal-dialog-centered"
      >
        <ModalHeader toggle={() => setDetail(null)} className="border-bottom">
          <IntlMessages
            id="shopee.workspace.orders.detail-title"
            values={{ orderSn: detail && detail.order_sn }}
          />
        </ModalHeader>
        <ModalBody className="p-4">
          {detailLoading ? (
            <div className="text-center py-4 text-muted">
              <i className="simple-icon-refresh spin mr-2" />{" "}
              <IntlMessages id="shopee.workspace.orders.detail-loading" />
            </div>
          ) : null}
          {detail && detail.loadError ? (
            <Alert color="warning">
              <IntlMessages id="shopee.workspace.orders.detail-load-error" />
            </Alert>
          ) : null}
          {detail && !detailLoading ? (
            <React.Fragment>
              <Row
                className="mb-3 p-3 rounded"
                style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
              >
                <Col md="6">
                  <p className="mb-1">
                    <strong className="text-muted">
                      <IntlMessages id="shopee.workspace.common.customer" />:{" "}
                    </strong>
                    <strong className="text-dark">
                      {detail.recipient_name || detail.buyer_username || "—"}
                    </strong>
                  </p>
                  <p className="mb-0">
                    <strong className="text-muted">
                      <IntlMessages id="shopee.workspace.orders.phone-label" />{" "}
                    </strong>
                    <span className="text-dark">
                      {detail.recipient_phone || "—"}
                    </span>
                  </p>
                </Col>
                <Col md="6">
                  <p className="mb-1">
                    <strong className="text-muted">
                      <IntlMessages id="shopee.workspace.returns.col.status" />
                      :{" "}
                    </strong>
                    <Badge
                      color={detailStatus.color}
                      pill
                    >
                      {detailStatus.label ? (
                        <IntlMessages id={detailStatus.label} />
                      ) : (
                        detailStatus.text
                      )}
                    </Badge>
                  </p>
                  <p className="mb-0">
                    <strong className="text-muted">
                      <IntlMessages id="shopee.workspace.orders.tracking-label" />{" "}
                    </strong>
                    <strong className="text-dark">
                      {detail.tracking_number || "—"}
                    </strong>
                  </p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="4" className="mb-2 mb-md-0">
                  <small className="text-muted d-block">
                    <IntlMessages id="shopee.workspace.orders.total-shopee" />
                  </small>
                  <strong className="text-dark">
                    {formatMoney(detail.total_amount, detail.currency)}
                  </strong>
                </Col>
                <Col md="4" className="mb-2 mb-md-0">
                  <small className="text-muted d-block">
                    <IntlMessages id="shopee.workspace.orders.created-at" />
                  </small>
                  <strong className="text-dark">
                    {dateParts(detail.order_create_time).join(" ").trim() ||
                      "—"}
                  </strong>
                </Col>
                <Col md="4">
                  <small className="text-muted d-block">
                    <IntlMessages id="shopee.workspace.orders.ship-deadline" />
                  </small>
                  <strong className="text-dark">
                    {dateParts(detail.ship_by_date).join(" ").trim() || "—"}
                  </strong>
                </Col>
              </Row>

              <h6 className="font-weight-bold text-dark mb-3">
                <IntlMessages
                  id="shopee.workspace.orders.items-title"
                  values={{ count: (detail.items || []).length }}
                />
              </h6>
              {(detail.items || []).map((item, index) => {
                const quantity = Number(
                  item.model_quantity_purchased ??
                    item.quantity_purchased ??
                    item.quantity,
                );
                const price =
                  item.discounted_price ??
                  item.model_discounted_price ??
                  item.original_price ??
                  item.model_original_price;
                return (
                  <Card
                    key={
                      item.id ||
                      `${item.item_id || "item"}-${item.model_id || index}`
                    }
                    className="mb-2 border-0"
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <CardBody className="py-2 d-flex align-items-center justify-content-between">
                      <span className="font-weight-semibold text-dark">
                        {item.item_name ||
                          item.model_name ||
                          item.item_sku ||
                          item.model_sku ||
                          "—"}
                        {hasValue(price) ? (
                          <small className="text-muted d-block">
                            <IntlMessages
                              id="shopee.workspace.orders.price-per-item"
                              values={{
                                price: formatMoney(price, detail.currency),
                              }}
                            />
                          </small>
                        ) : null}
                      </span>
                      <Badge
                        color="light"
                        pill
                        className="font-weight-bold px-3 py-1"
                      >
                        x
                        {Number.isFinite(quantity) && quantity > 0
                          ? quantity
                          : "—"}
                      </Badge>
                    </CardBody>
                  </Card>
                );
              })}
            </React.Fragment>
          ) : null}
        </ModalBody>
        <ModalFooter className="border-top">
          {detail ? (
            <Button
              color="primary"
              className="rounded-pill px-4"
              style={{ background: "#EE4D2D", border: "none" }}
              disabled={workspace.busy[`create-bill-${detail.id}`]}
              onClick={() =>
                workspace.runAction(
                  `create-bill-${detail.id}`,
                  (key) => ShopeeV2Api.createBill(detail.id, key),
                  intl.formatMessage({
                    id: "shopee.workspace.orders.bill-created",
                  }),
                )
              }
            >
              <IntlMessages id="shopee.workspace.orders.create-bill" />
            </Button>
          ) : null}
          <Button
            color="light"
            className="rounded-pill px-3"
            onClick={() => setDetail(null)}
          >
            <IntlMessages id="shopee.workspace.common.close" />
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
});
